/**
 * AskMeri — tenant-aware contextual assistant drawer.
 *
 * Scope is enforced server-side: the API derives the user's accessible
 * businesses from their session (firm/tenant scoping), so this component only
 * sends the question plus the currently selected business (a hint that
 * narrows the already-authorized scope) and the current screen name.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles, X, SendHorizonal, RotateCcw } from 'lucide-react';
import { API_BASE, apiFetch } from '../api/base';
import { usePortCo } from '../context/PortCoContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  at?: string;
}

const PAGE_TITLES: Record<string, string> = {
  '/': 'Executive Dashboard',
  '/dashboard': 'Executive Dashboard',
  '/workstreams': 'Workstreams',
  '/fleet': 'Agent Fleet',
  '/approvals': 'Approvals',
  '/claims': 'Claims',
  '/integrations': 'Integration Hub',
  '/value': 'Value Report',
  '/audit': 'Audit Log',
  '/settings': 'Settings',
};

const SUGGESTIONS: Record<string, string[]> = {
  '/': ['How are we doing this week?', 'Explain the key KPIs and any anomalies'],
  '/dashboard': ['How are we doing this week?', 'Explain the key KPIs and any anomalies'],
  '/approvals': ["What's pending my approval?", 'Which items are highest impact?'],
  '/claims': ['Why are claims being denied?', 'What is the total denied amount?'],
  '/fleet': ['How are the agents performing?', 'When did each agent last run?'],
  '/workstreams': ['Summarize each workstream status', 'Which workstream needs attention?'],
};

export const AskMeri: React.FC = () => {
  const { selectedPortco } = usePortCo();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [scopeTenants, setScopeTenants] = useState<Array<{ portcoId: string; name: string }>>([]);
  const [followUps, setFollowUps] = useState<string[]>([]);
  const listRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<ChatMessage[]>([]);

  const pageKey = PAGE_TITLES[location.pathname]
    ? Object.keys(PAGE_TITLES).find((k) => PAGE_TITLES[k] === PAGE_TITLES[location.pathname])!
    : location.pathname;

  const loadHistory = useCallback(async () => {
    try {
      const portcoKey = selectedPortco === 'all' ? 'all' : selectedPortco;
      const res = await apiFetch(`${API_BASE}/ask?portcoId=${encodeURIComponent(portcoKey)}`);
      if (!res.ok) return;
      const data = await res.json();
      setMessages(data.messages || []);
      setScopeTenants(data.scopeTenants || []);
      setFollowUps(data.followUps || []);
    } catch {
      /* history is best-effort */
    }
  }, [selectedPortco]);

  useEffect(() => {
    if (open) loadHistory();
  }, [open, loadHistory]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages, busy]);

  const ask = async (question: string) => {
    if (!question.trim() || busy) return;
    const portcoKey = selectedPortco === 'all' ? 'all' : selectedPortco;
    setMessages((m) => [...m, { role: 'user', content: question }]);
    setInput('');
    setBusy(true);
    setFollowUps([]); // stale suggestions leave while the new answer is pending
    try {
      const res = await apiFetch(`${API_BASE}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          portcoId: selectedPortco === 'all' ? undefined : selectedPortco,
          page: PAGE_TITLES[location.pathname] || location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'AskMeri is unavailable');
      setMessages(data.conversation && data.conversation.length ? data.conversation : [...messagesRef.current, { role: 'assistant', content: data.answer }]);
      if (data.scopeTenants) setScopeTenants(data.scopeTenants);
      setFollowUps(data.followUps || []);
    } catch (err: any) {
      setMessages((m) => [...m, { role: 'assistant', content: err?.message || 'AskMeri could not answer right now. Try again in a moment.' }]);
    } finally {
      setBusy(false);
    }
  };

  // keep a ref of messages for the fallback path above
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const resetConversation = async () => {
    try {
      const portcoKey = selectedPortco === 'all' ? 'all' : selectedPortco;
      await apiFetch(`${API_BASE}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset: true, portcoId: portcoKey }),
      });
    } catch { /* best-effort */ }
    setMessages([]);
    setFollowUps([]);
  };

  const suggestions = SUGGESTIONS[pageKey] || ['What can you help me with?', 'Summarize how the business is doing'];

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-mantis-500 text-white pl-3.5 pr-4 py-2.5 shadow-lg hover:bg-mantis-600 transition-colors"
        title="AskMeri — ask about your data"
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-semibold">AskMeri</span>
      </button>

      {/* Slide-over drawer */}
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/25" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-mantis-500" />
                  <h3 className="text-sm font-bold text-slate-800">AskMeri</h3>
                </div>
                <p className="text-[11px] text-slate-400">
                  {PAGE_TITLES[location.pathname] ? `Context: ${PAGE_TITLES[location.pathname]}` : 'Contextual assistant'}
                  {scopeTenants.length > 0 && scopeTenants.length <= 3 && (
                    <span> · {scopeTenants.map((t) => t.name).join(', ')}</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={resetConversation} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100" title="New conversation">
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button onClick={() => setOpen(false)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100" title="Close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
              {messages.length === 0 && !busy && (
                <div className="pt-6 space-y-3">
                  <div className="text-center">
                    <Sparkles className="w-6 h-6 text-mantis-400 mx-auto" />
                    <p className="mt-2 text-xs text-slate-500">Ask about your KPIs, claims, approvals and agents.</p>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center pt-2">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => ask(s)}
                        className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-mantis-300 hover:text-mantis-600 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {m.role === 'assistant' ? (
                    <div className="askmeri-md max-w-[85%] rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed bg-white border border-slate-200 text-slate-700">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="max-w-[85%] rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap bg-mantis-500 text-white">
                      {m.content}
                    </div>
                  )}
                </div>
              ))}
              {!busy && followUps.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {followUps.map((f) => (
                    <button
                      key={f}
                      onClick={() => ask(f)}
                      className="text-xs px-3 py-1.5 rounded-full border border-mantis-200 bg-mantis-50 text-mantis-700 hover:border-mantis-400 hover:bg-mantis-100 transition-colors text-left"
                      title="Ask this follow-up"
                    >
                      {f}
                    </button>
                  ))}
                </div>
              )}
              {busy && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-[13px] text-slate-400">
                    AskMeri is thinking…
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); ask(input); }}
              className="p-3 border-t border-slate-200 flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about your data…"
                className="flex-1 text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mantis-200"
                disabled={busy}
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="rounded-lg bg-mantis-500 text-white p-2 hover:bg-mantis-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <SendHorizonal className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
